// Difficult sections on the map: each one drawn over the route line in its
// grade's colour (orange T3-T4, red T5-T6), a little wider than the route and
// with a white casing, under the markers — on the planner map and the hike's
// map page. Picking one (a row, or the line itself on the hike map) fits the
// map to it, keeps it lit with a soft glow and opens a small popup
// "Alpine route / T4 · 700 m" (canvas screen 9c).
//
// The data and layer specs are plain functions, unit-tested without Mapbox; the
// controller talks to the map only through the few calls it names, so it is
// tested with a fake map.

import { decode } from '@googlemaps/polyline-codec';
import { MapLayerCategory } from '../layer-registry';
import type { RouteAlert } from './routeAlertsModel';
import type { SectionCallout } from './sacSections';
import type { PagerView } from './routeAlertGroups';
import { TERRAIN_GLYPH_PATH, TERRAIN_TRIANGLE_PATH, TERRAIN_VIEWBOX, sacColour } from './sacSections';

export const SECTION_SOURCE_ID = 'route-alert-sections';
export const SECTION_GLOW_LAYER_ID = 'route-alert-sections-glow';
export const SECTION_CASING_LAYER_ID = 'route-alert-sections-casing';
export const SECTION_LINE_LAYER_ID = 'route-alert-sections-line';

/**
 * Annotations band: above the route (planner 7-11, hike map 7-10) and below
 * the markers and labels (12+); hazard markers are in the Top band anyway.
 */
export const SECTION_LAYER_CATEGORY = MapLayerCategory.Annotations;
export const SECTION_LAYER_Z = 11.5;

/** The route line is 5 px with a 9 px border: the section a little wider. */
export const SECTION_LINE_WIDTH = 7;
export const SECTION_CASING_WIDTH = 11;
export const SECTION_GLOW_WIDTH = 24;

export const FOCUS_PADDING_PX = 64;
export const FOCUS_MAX_ZOOM = 16;

export type LngLat = [number, number];

/** A section's line as [lng, lat] points; empty for a missing or broken polyline. */
export function decodeSectionLine(encoded: string | null | undefined): LngLat[] {
  if (typeof encoded !== 'string' || encoded.length === 0) return [];
  try {
    return decode(encoded, 5)
      .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180)
      .map(([lat, lng]) => [lng, lat] as LngLat);
  } catch {
    return [];
  }
}

/** [[west, south], [east, north]] around the points; null for none. */
export function sectionBounds(points: LngLat[]): [LngLat, LngLat] | null {
  if (!points.length) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  points.forEach(([lng, lat]) => {
    west = Math.min(west, lng);
    east = Math.max(east, lng);
    south = Math.min(south, lat);
    north = Math.max(north, lat);
  });
  return [[west, south], [east, north]];
}

/** The alerts that can be drawn: difficult sections with a line of 2+ points. */
export function drawableSections(alerts: RouteAlert[]): Array<{ alert: RouteAlert; line: LngLat[] }> {
  return alerts
    .filter((alert) => alert.kind === 'difficult_terrain' && alert.sacGrade !== null)
    .map((alert) => ({ alert, line: decodeSectionLine(alert.encodedPolyline) }))
    .filter(({ line }) => line.length >= 2);
}

/** The GeoJSON the section layers draw; the selected one carries `selected: true`. */
export function sectionFeatureCollection(alerts: RouteAlert[], selectedId: string | null = null) {
  return {
    type: 'FeatureCollection' as const,
    features: drawableSections(alerts).map(({ alert, line }) => ({
      type: 'Feature' as const,
      properties: {
        id: alert.id,
        grade: alert.sacGrade,
        colour: sacColour(alert.sacGrade as number),
        selected: alert.id === selectedId,
      },
      geometry: { type: 'LineString' as const, coordinates: line },
    })),
  };
}

/** Glow (selected only), white casing, coloured line — back to front. */
export function sectionLayerSpecs(): any[] {
  const roundLine = { 'line-join': 'round', 'line-cap': 'round' };
  return [
    {
      id: SECTION_GLOW_LAYER_ID,
      type: 'line',
      source: SECTION_SOURCE_ID,
      filter: ['==', ['get', 'selected'], true],
      layout: roundLine,
      paint: { 'line-color': ['get', 'colour'], 'line-width': SECTION_GLOW_WIDTH, 'line-opacity': 0.3, 'line-blur': 2 },
    },
    {
      id: SECTION_CASING_LAYER_ID,
      type: 'line',
      source: SECTION_SOURCE_ID,
      layout: roundLine,
      paint: { 'line-color': '#ffffff', 'line-width': SECTION_CASING_WIDTH },
    },
    {
      id: SECTION_LINE_LAYER_ID,
      type: 'line',
      source: SECTION_SOURCE_ID,
      layout: roundLine,
      paint: { 'line-color': ['get', 'colour'], 'line-width': SECTION_LINE_WIDTH },
    },
  ];
}

export interface FocusPadding {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface FocusPaddingInput {
  mapRect: { left: number; right: number; top: number; bottom: number } | null;
  /** The right edge of a panel over the map's left side (the planner's routes panel); null when none. */
  leftPanelRight: number | null;
  /** The left edge of a column over the map's right side (the planner's Actions); null when none. */
  dockLeft: number | null;
  /** The top edge of a panel over the map's bottom (the hike map's elevation profile); null when none. */
  bottomPanelTop?: number | null;
}

/**
 * Padding for fitting the map to a section so it lands in the part of the map
 * the panels over it leave open (the planner's side panels, the hike map's
 * elevation profile); the plain padding along an axis where they would leave
 * too little (a narrow or short window).
 */
export function focusPadding(
  { mapRect, leftPanelRight, dockLeft, bottomPanelTop = null }: FocusPaddingInput,
  base = FOCUS_PADDING_PX
): FocusPadding {
  const plain = { top: base, bottom: base, left: base, right: base };
  if (!mapRect) return plain;
  const left = base + (leftPanelRight === null ? 0 : Math.max(0, leftPanelRight - mapRect.left));
  const right = base + (dockLeft === null ? 0 : Math.max(0, mapRect.right - dockLeft));
  const bottom = base + (bottomPanelTop === null ? 0 : Math.max(0, mapRect.bottom - bottomPanelTop));
  const wideEnough = mapRect.right - mapRect.left - left - right >= 160;
  const tallEnough = mapRect.bottom - mapRect.top - base - bottom >= 160;
  return {
    top: base,
    bottom: tallEnough ? bottom : base,
    left: wideEnough ? left : base,
    right: wideEnough ? right : base,
  };
}

/* ---------- popup ---------- */

const SVG_NS = 'http://www.w3.org/2000/svg';
const CALLOUT_STYLE_ID = 'route-section-callout-styles';
export const CALLOUT_CLASS = 'route-section-popup';

/** The popup's look: the dark HiiKER pill of canvas 9c, over Mapbox's white box. */
const CALLOUT_CSS = `
.${CALLOUT_CLASS} .mapboxgl-popup-content{padding:8px 12px 8px 8px;border-radius:12px;background:#17181d;box-shadow:0 4px 14px rgba(0,0,0,.3)}
.${CALLOUT_CLASS} .mapboxgl-popup-tip{border-top-color:#17181d;border-bottom-color:#17181d}
.route-section-callout{display:flex;align-items:center;gap:8px;color:#fff;font-family:inherit}
.route-section-callout__text{display:flex;flex-direction:column;gap:2px}
.route-section-callout__title{font-size:12px;font-weight:700;white-space:nowrap}
.route-section-callout__detail{font-size:11px;font-weight:600;white-space:nowrap}
.route-section-callout__divider{width:1px;height:28px;margin:0 2px;background:rgba(255,255,255,.14)}
.route-section-callout__arrow{display:flex;align-items:center;justify-content:center;width:28px;height:28px;padding:0;border:0;border-radius:50%;background:#2d2f35;cursor:pointer}
.route-section-callout__arrow:hover:not(:disabled){background:#3a3c43}
.route-section-callout__arrow:focus-visible{outline:2px solid #fddf00;outline-offset:2px}
.route-section-callout__arrow:disabled{opacity:.35;cursor:default}
.route-section-callout__arrow path{fill:none;stroke:#fff;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.route-section-callout__count{min-width:30px;text-align:center;font-size:12px;font-weight:700;font-variant-numeric:tabular-nums}
`;

function ensureCalloutStyles(doc: Document): void {
  if (doc.getElementById(CALLOUT_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = CALLOUT_STYLE_ID;
  style.textContent = CALLOUT_CSS;
  doc.head.appendChild(style);
}

/** The terrain triangle as an SVG element (the row icon's twin). */
export function terrainIconElement(doc: Document, colour: string, width = 26): SVGSVGElement {
  const svg = doc.createElementNS(SVG_NS, 'svg') as SVGSVGElement;
  svg.setAttribute('viewBox', TERRAIN_VIEWBOX);
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(Math.round((width * 82) / 90)));
  svg.setAttribute('aria-hidden', 'true');
  const triangle = doc.createElementNS(SVG_NS, 'path');
  triangle.setAttribute('d', TERRAIN_TRIANGLE_PATH);
  triangle.setAttribute('fill', colour);
  triangle.setAttribute('stroke', '#ffffff');
  triangle.setAttribute('stroke-width', '7');
  triangle.setAttribute('stroke-linejoin', 'round');
  const glyph = doc.createElementNS(SVG_NS, 'path');
  glyph.setAttribute('d', TERRAIN_GLYPH_PATH);
  glyph.setAttribute('fill', '#ffffff');
  svg.appendChild(triangle);
  svg.appendChild(glyph);
  return svg;
}

/** Light text colours of the tags (orange / red), readable on the dark pill. */
const DETAIL_COLOUR: Record<SectionCallout['level'], string> = { warning: '#ffa34d', danger: '#ff8a90' };

/** The callout's pager (‹ 1 / 3 ›): what it shows, and what its arrows do. */
export interface SectionCalloutPager extends PagerView {
  onPrevious: () => void;
  onNext: () => void;
}

function pagerArrow(doc: Document, direction: 'previous' | 'next', pager: SectionCalloutPager): HTMLButtonElement {
  const button = doc.createElement('button');
  button.type = 'button';
  button.className = 'route-section-callout__arrow';
  button.setAttribute('data-test', `route-section-callout-${direction}`);
  const enabled = direction === 'previous' ? pager.canPrevious : pager.canNext;
  button.disabled = !enabled;
  button.setAttribute('aria-label', direction === 'previous' ? pager.previousLabel : pager.nextLabel);
  const svg = doc.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '14');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('aria-hidden', 'true');
  const path = doc.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', direction === 'previous' ? 'M10 3 L5 8 L10 13' : 'M6 3 L11 8 L6 13');
  svg.appendChild(path);
  button.appendChild(svg);
  button.addEventListener('click', (event) => {
    // The map under the popup must not take this click as one of its own.
    event.stopPropagation();
    if (button.disabled) return;
    if (direction === 'previous') pager.onPrevious();
    else pager.onNext();
  });
  return button;
}

/** Keyboard focus on the arrow just used, or on the other one where the group ends. */
function focusPagerArrow(root: HTMLElement, direction: 'previous' | 'next'): void {
  const other = direction === 'next' ? 'previous' : 'next';
  const same = root.querySelector(`[data-test="route-section-callout-${direction}"]`) as HTMLButtonElement | null;
  const fallback = root.querySelector(`[data-test="route-section-callout-${other}"]`) as HTMLButtonElement | null;
  const target = same && !same.disabled ? same : fallback;
  try {
    target?.focus({ preventScroll: true });
  } catch {
    // Not focusable in this document.
  }
}

/** The popup's arrows and count, after a divider. */
function appendPager(doc: Document, root: HTMLElement, pager: SectionCalloutPager): void {
  const divider = doc.createElement('span');
  divider.className = 'route-section-callout__divider';
  divider.setAttribute('aria-hidden', 'true');
  root.appendChild(divider);
  root.appendChild(pagerArrow(doc, 'previous', pager));
  const count = doc.createElement('span');
  count.className = 'route-section-callout__count';
  count.setAttribute('aria-live', 'polite');
  count.setAttribute('aria-atomic', 'true');
  const seen = doc.createElement('span');
  seen.setAttribute('aria-hidden', 'true');
  seen.setAttribute('data-test', 'route-section-callout-count');
  seen.textContent = pager.countText;
  const heard = doc.createElement('span');
  heard.className = 'visually-hidden';
  heard.textContent = pager.positionLabel;
  count.appendChild(seen);
  count.appendChild(heard);
  root.appendChild(count);
  root.appendChild(pagerArrow(doc, 'next', pager));
}

/** The popup's content: icon, name, "T4 · 700 m"; and, for a group, its pager. */
export function sectionCalloutElement(doc: Document, callout: SectionCallout, pager: SectionCalloutPager | null = null): HTMLElement {
  const root = doc.createElement('div');
  root.className = 'route-section-callout';
  root.setAttribute('role', 'status');
  root.setAttribute('data-test', 'route-section-callout');
  root.appendChild(terrainIconElement(doc, callout.colour));
  const text = doc.createElement('span');
  text.className = 'route-section-callout__text';
  const title = doc.createElement('span');
  title.className = 'route-section-callout__title';
  title.textContent = callout.title;
  const detail = doc.createElement('span');
  detail.className = 'route-section-callout__detail';
  detail.style.color = DETAIL_COLOUR[callout.level];
  detail.textContent = callout.detail;
  text.appendChild(title);
  text.appendChild(detail);
  root.appendChild(text);
  if (pager) appendPager(doc, root, pager);
  return root;
}

/* ---------- controller ---------- */

export interface SectionMap {
  getSource(id: string): any;
  getLayer(id: string): any;
  isStyleLoaded?(): boolean;
  fitBounds(bounds: [LngLat, LngLat], options?: Record<string, unknown>): void;
  on(type: string, layerOrHandler: any, handler?: any): void;
  off(type: string, layerOrHandler: any, handler?: any): void;
  once?(type: string, handler: any): void;
  getCanvas?(): { style: { cursor: string } };
}

export interface SectionRegistry {
  addLayer(layer: any, category: any, opts?: { z?: number; source?: { id: string; spec: any; replace?: boolean } }): void;
  removeLayer(id: string, removeOrphanSource?: boolean): void;
}

export interface SectionPopup {
  setLngLat(lngLat: LngLat): SectionPopup;
  setDOMContent(element: HTMLElement): SectionPopup;
  addTo(map: any): SectionPopup;
  remove(): void;
  /** Mapbox's popup events: `close` when a map click (closeOnClick) or remove() closes it. */
  on?(type: string, handler: () => void): unknown;
}

/**
 * `window.map` when it is a Mapbox map. Before the page makes its map, the
 * name resolves to the `<div id="map">` element (named access on window).
 */
export function mapboxMapOf(candidate: unknown): SectionMap | null {
  const map = candidate as any;
  return map && typeof map.getSource === 'function' && typeof map.fitBounds === 'function' && typeof map.on === 'function'
    ? (map as SectionMap)
    : null;
}

export interface SectionHighlighterDeps {
  getMap: () => SectionMap | null | undefined;
  getRegistry: () => SectionRegistry | null | undefined;
  /** A Mapbox popup (mapboxgl.Popup with CALLOUT_CLASS); null where there's none to show. */
  createPopup?: () => SectionPopup | null;
  /** The popup words for a section, in the viewer's language and units. */
  calloutFor: (alert: RouteAlert) => SectionCallout;
  /** The hike map lets the line itself be clicked; the planner keeps clicks for editing. */
  clickable?: boolean;
  /** Where the map is left open by panels over it, for fitting to a section. */
  focusPadding?: () => FocusPadding | number;
  /** The picked section changed: an id when one is focused, null when it was let go. */
  onSelectionChange?: (sectionId: string | null) => void;
  /** The popup's pager for a section with more of its grade on the route; null for none. */
  pagerFor?: (alert: RouteAlert) => SectionCalloutPager | null;
  doc?: Document;
}

export class RouteSectionHighlighter {
  private alerts: RouteAlert[] = [];
  private selectedId: string | null = null;
  private popup: SectionPopup | null = null;
  private boundMap: SectionMap | null = null;
  private retrying = false;
  private disposed = false;

  constructor(private readonly deps: SectionHighlighterDeps) {}

  /** The current warnings: their sections are drawn; a picked one that's gone is let go. */
  setAlerts(alerts: RouteAlert[]): void {
    if (this.disposed) return;
    this.alerts = Array.isArray(alerts) ? alerts : [];
    if (this.selectedId && !drawableSections(this.alerts).some(({ alert }) => alert.id === this.selectedId)) {
      this.selectedId = null;
      this.removePopup();
      this.notify();
    }
    this.sync();
  }

  get selected(): string | null {
    return this.selectedId;
  }

  /**
   * Picks a section: lit, popup at its middle, and (by default) the map fitted
   * to it. False when it isn't one that can be drawn.
   */
  focus(
    sectionId: string,
    { fit = true, focusArrow = null }: { fit?: boolean; focusArrow?: 'previous' | 'next' | null } = {},
  ): boolean {
    if (this.disposed) return false;
    const found = drawableSections(this.alerts).find(({ alert }) => alert.id === sectionId);
    const map = mapboxMapOf(this.deps.getMap());
    if (!found || !map) return false;
    this.selectedId = sectionId;
    this.sync();
    const bounds = sectionBounds(found.line);
    if (fit && bounds) {
      try {
        const padding = this.deps.focusPadding?.() ?? FOCUS_PADDING_PX;
        map.fitBounds(bounds, { padding, maxZoom: FOCUS_MAX_ZOOM, duration: 600 });
      } catch (error) {
        console.warn('[route_alerts] could not fit the map to the section', error);
      }
    }
    this.showPopup(map, found.alert, found.line, focusArrow);
    this.notify();
    return true;
  }

  /** Lets the picked section go (unlit, no popup). */
  clearSelection(): void {
    if (!this.selectedId) return;
    this.selectedId = null;
    this.removePopup();
    this.sync();
    this.notify();
  }

  private notify(): void {
    try {
      this.deps.onSelectionChange?.(this.selectedId);
    } catch {
      // The host's bookkeeping must not break the map.
    }
  }

  dispose(): void {
    this.disposed = true;
    this.removePopup();
    this.unbind();
    const map = mapboxMapOf(this.deps.getMap());
    const registry = this.deps.getRegistry();
    if (!map || !registry) return;
    try {
      [SECTION_LINE_LAYER_ID, SECTION_CASING_LAYER_ID, SECTION_GLOW_LAYER_ID].forEach((id) => {
        if (map.getLayer(id)) registry.removeLayer(id, true);
      });
    } catch (error) {
      console.warn('[route_alerts] could not remove the section layers', error);
    }
  }

  /** Brings the map in line with the sections: source data, layers, listeners. Idempotent. */
  readonly sync = (): void => {
    if (this.disposed) return;
    const map = mapboxMapOf(this.deps.getMap());
    const registry = this.deps.getRegistry();
    if (!map || !registry) return;
    const data = sectionFeatureCollection(this.alerts, this.selectedId);
    try {
      const source = map.getSource(SECTION_SOURCE_ID);
      // Nothing drawn and nothing to draw: leave the map alone.
      if (data.features.length === 0 && !source) return;
      this.bind(map);
      if (source && typeof source.setData === 'function') {
        source.setData(data);
      }
      sectionLayerSpecs().forEach((layer) => {
        if (map.getLayer(layer.id)) return;
        registry.addLayer(layer, SECTION_LAYER_CATEGORY, {
          z: SECTION_LAYER_Z,
          source: { id: SECTION_SOURCE_ID, spec: { type: 'geojson', data } },
        });
      });
    } catch (error) {
      // Most often "Style is not done loading": try again once the map settles.
      console.warn('[route_alerts] could not draw the sections yet', error);
      this.retryWhenIdle(map);
    }
  };

  private retryWhenIdle(map: SectionMap): void {
    if (this.retrying || typeof map.once !== 'function') return;
    this.retrying = true;
    map.once('idle', () => {
      this.retrying = false;
      this.sync();
    });
  }

  /**
   * `focusArrow`: a pager step made from that arrow — the new popup's same
   * arrow takes the keyboard focus (the other one at the end of the group),
   * so stepping on with the keyboard keeps working.
   */
  private showPopup(map: SectionMap, alert: RouteAlert, line: LngLat[], focusArrow: 'previous' | 'next' | null = null): void {
    this.removePopup();
    const popup = this.deps.createPopup?.() ?? null;
    if (!popup) return;
    const doc = this.deps.doc ?? (typeof document === 'undefined' ? null : document);
    if (!doc) return;
    ensureCalloutStyles(doc);
    const at: LngLat = Number.isFinite(alert.longitude) && Number.isFinite(alert.latitude)
      ? [alert.longitude, alert.latitude]
      : line[Math.floor(line.length / 2)];
    try {
      const pager = this.pagerFor(alert);
      const element = sectionCalloutElement(doc, this.deps.calloutFor(alert), pager);
      this.popup = popup.setLngLat(at).setDOMContent(element).addTo(map);
      if (pager && focusArrow) focusPagerArrow(element, focusArrow);
      // Closed by a click on the map: the section is let go with it.
      popup.on?.('close', () => {
        if (this.popup !== popup) return; // replaced or removed by us
        this.popup = null;
        if (!this.selectedId) return;
        this.selectedId = null;
        this.sync();
        this.notify();
      });
    } catch (error) {
      console.warn('[route_alerts] could not show the section popup', error);
      this.popup = null;
    }
  }

  private pagerFor(alert: RouteAlert): SectionCalloutPager | null {
    try {
      return this.deps.pagerFor?.(alert) ?? null;
    } catch {
      // No arrows rather than no popup.
      return null;
    }
  }

  private removePopup(): void {
    const popup = this.popup;
    this.popup = null;
    try {
      popup?.remove();
    } catch {
      // Already gone with its map.
    }
  }

  private readonly onStyleLoad = (): void => this.sync();

  private readonly onClick = (event: any): void => {
    const id = event?.features?.[0]?.properties?.id;
    if (typeof id === 'string') this.focus(id, { fit: false });
  };

  private readonly onMouseEnter = (): void => {
    const canvas = this.boundMap?.getCanvas?.();
    if (canvas) canvas.style.cursor = 'pointer';
  };

  private readonly onMouseLeave = (): void => {
    const canvas = this.boundMap?.getCanvas?.();
    if (canvas) canvas.style.cursor = '';
  };

  // A style switch drops the source and layers: put them back on `style.load`.
  private bind(map: SectionMap): void {
    if (this.boundMap === map) return;
    this.unbind();
    map.on('style.load', this.onStyleLoad);
    if (this.deps.clickable) {
      map.on('click', SECTION_LINE_LAYER_ID, this.onClick);
      map.on('mouseenter', SECTION_LINE_LAYER_ID, this.onMouseEnter);
      map.on('mouseleave', SECTION_LINE_LAYER_ID, this.onMouseLeave);
    }
    this.boundMap = map;
  }

  private unbind(): void {
    const map = this.boundMap;
    if (!map) return;
    try {
      map.off('style.load', this.onStyleLoad);
      if (this.deps.clickable) {
        map.off('click', SECTION_LINE_LAYER_ID, this.onClick);
        map.off('mouseenter', SECTION_LINE_LAYER_ID, this.onMouseEnter);
        map.off('mouseleave', SECTION_LINE_LAYER_ID, this.onMouseLeave);
      }
    } catch {
      // A removed map.
    }
    this.boundMap = null;
  }
}
